

import { useState } from "react"
import Button from "../ui/button"
import { Card } from "../ui/card"
import { BookOpen, Clock, Users, Target, FileText, RefreshCw } from "lucide-react"

const LessonPlanGenerator = ({ onSubmit }) => {
  const [subject, setSubject] = useState("")
  const [topic, setTopic] = useState("")
  const [gradeLevel, setGradeLevel] = useState("middle")
  const [duration, setDuration] = useState(45)
  const [objectives, setObjectives] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState("basic")

  const gradeLevels = {
    elementary: "Elementary School (Grades K-5)",
    middle: "Middle School (Grades 6-8)",
    highSchool: "High School (Grades 9-12)",
    college: "College/University",
  }

  const generateLessonPlan = async () => {
    if (!subject || !topic) return

    setIsLoading(true)

    try {
      // Make a direct API call to the lesson plan generator endpoint
      const response = await fetch("/api/generate-lesson-plan", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subject,
          topic,
          gradeLevel: gradeLevels[gradeLevel],
          duration,
          objectives,
        }),
      })

      const data = await response.json()

      if (data.success) {
        // Format the lesson plan for display in the chatbox
        const formattedContent = `# Lesson Plan: ${topic} (${subject})\n\nFor ${gradeLevels[gradeLevel]} • ${duration} minutes\n\n${data.lessonPlan}`

        // Pass the configuration and formatted content to the parent component
        if (onSubmit) {
          const config = {
            subject,
            topic,
            gradeLevel: gradeLevels[gradeLevel],
            duration,
            objectives,
            toolType: "lesson",
            type: "lesson",
            response: formattedContent, // Add the response directly to the config
          }
          onSubmit(config)
        }
      } else {
        console.error("Error generating lesson plan:", data.error)
        // If the API call fails, fall back to using the chat API
        fallbackToChatAPI()
      }
    } catch (error) {
      console.error("Error calling lesson plan generator API:", error)
      // If there's an error with the API call, fall back to using the chat API
      fallbackToChatAPI()
    } finally {
      setIsLoading(false)
    }
  }

  // Fallback method that uses the chat API through the parent component
  const fallbackToChatAPI = () => {
    const formattedContent =
      `# Lesson Plan: ${topic} (${subject})\n\n` +
      `For ${gradeLevels[gradeLevel]} • ${duration} minutes\n\n` +
      "I'll create a detailed lesson plan with the following sections:\n\n" +
      "1. Lesson Title and Overview\n" +
      "2. Learning Objectives\n" +
      "3. Materials and Resources Needed\n" +
      "4. Lesson Structure with Timing\n" +
      "5. Differentiation Strategies\n" +
      "6. Extensions/Homework\n" +
      "7. Assessment Criteria\n\n" +
      "Please wait while I generate the lesson plan..."

    // Pass the configuration and formatted content to the parent component
    if (onSubmit) {
      const config = {
        subject,
        topic,
        gradeLevel: gradeLevels[gradeLevel],
        duration,
        objectives,
        toolType: "lesson",
        type: "lesson",
        response: formattedContent, // Add the response directly to the config
        fallback: true,
      }
      onSubmit(config)
    }
  }

  return (
    <Card className="p-4 bg-white shadow-lg border border-gray-200 rounded-xl mb-6">
      <div className="flex items-center mb-4">
        <BookOpen className="mr-2 h-5 w-5 text-green-600" />
        <h3 className="text-lg font-semibold text-gray-800">Lesson Plan Generator</h3>
      </div>

      <div className="mb-4">
        <div className="grid grid-cols-2 mb-4">
          <button
            onClick={() => setActiveTab("basic")}
            className={`py-2 px-4 text-center ${
              activeTab === "basic"
                ? "bg-green-100 text-green-800 border-b-2 border-green-500"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Basic Info
          </button>
          <button
            onClick={() => setActiveTab("details")}
            className={`py-2 px-4 text-center ${
              activeTab === "details"
                ? "bg-green-100 text-green-800 border-b-2 border-green-500"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Details
          </button>
        </div>

        {activeTab === "basic" && (
          <div className="p-4 bg-gray-50 rounded-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., Math, Science, History"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., Fractions, Photosynthesis"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="inline h-4 w-4 mr-1" />
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  min="15"
                  max="120"
                  step="5"
                  value={duration}
                  onChange={(e) => setDuration(Number.parseInt(e.target.value))}
                  className="w-full p-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Users className="inline h-4 w-4 mr-1" />
                  Grade Level
                </label>
                <select
                  value={gradeLevel}
                  onChange={(e) => setGradeLevel(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  {Object.entries(gradeLevels).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {activeTab === "details" && (
          <div className="p-4 bg-gray-50 rounded-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Target className="inline h-4 w-4 mr-1" />
                Learning Objectives (optional)
              </label>
              <textarea
                value={objectives}
                onChange={(e) => setObjectives(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-gray-900 min-h-[100px] focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter specific learning objectives, or leave blank for AI to generate them"
              />
              <p className="text-xs text-gray-500 mt-1">
                Tip: Good objectives are specific and measurable. Example: "Students will be able to solve quadratic
                equations using the quadratic formula."
              </p>
            </div>
          </div>
        )}
      </div>

      <Button
        onClick={generateLessonPlan}
        disabled={!subject || !topic || isLoading}
        className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
      >
        {isLoading ? (
          <>
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            Generating Lesson Plan...
          </>
        ) : (
          <>
            <FileText className="h-4 w-4 mr-2" />
            Generate Lesson Plan
          </>
        )}
      </Button>
    </Card>
  )
}

export default LessonPlanGenerator
